import { useCallback, useEffect, useRef, useState } from 'react';
import { FLOOR_LAYOUTS, WALL_LAYOUTS } from '../shared/layouts/templates';
import { DEFAULT_FRIEZE_MIX } from '../shared/layouts/friezeMixes';
import { MaterialSettings, ProjectState, ProposalClientInfo, StoredProject } from '../shared/types';
import { AuthGate, AccessInfo } from './components/AuthGate';
import { Icon } from './components/Icon';
import { Inspector } from './components/Inspector';
import { LeftPanel, PanelId } from './components/LeftPanel';
import { ProposalDialog } from './components/ProposalDialog';
import { Visualizer } from './components/Visualizer';
import { clientHeaders } from './clientIdentity';
import {
  buildProposal as buildProposalRequest,
  captureRoom3dDataUrl,
  ProposalResult,
  renderLayoutDataUrl,
} from './proposal';
import { useProjectStore } from './store/useProjectStore';

type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

const DEFAULT_MATERIALS: MaterialSettings = {
  tileThickness: 9,
  glueBagSize: 25,
  groutPackSize: 5,
  gluePricePerKg: 42,
  groutPricePerKg: 190,
  reservePercent: 10,
  tilesPerPack: {},
};

/** Миграция старых проектов: поля, добавленные после релиза */
const migrateProject = (state: ProjectState): ProjectState => {
  if (!state.ceiling) state.ceiling = { color: '#f4f2ee', glossy: true, spotlights: 4 };
  if (!state.fixtures) state.fixtures = [];
  if (!state.materials) state.materials = { ...DEFAULT_MATERIALS };
  if (!state.materials.tilesPerPack) state.materials.tilesPerPack = {};
  if (typeof state.materials.reservePercent !== 'number') state.materials.reservePercent = 10;
  if (!state.presetId) state.presetId = undefined;
  Object.values(state.surfaces).forEach((surface) => {
    if (!surface.friezeMix) surface.friezeMix = DEFAULT_FRIEZE_MIX;
  });
  return state;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const renderSvg = async (format: 'png' | 'jpeg', name: string) => {
  const canvas3d = document.querySelector<HTMLCanvasElement>('.room-canvas-3d');
  if (canvas3d) {
    const output = await new Promise<Blob>((resolve, reject) =>
      canvas3d.toBlob(
        (result) => result ? resolve(result) : reject(new Error('Не удалось создать файл')),
        `image/${format}`,
        0.94,
      ),
    );
    downloadBlob(output, `${name}.${format === 'jpeg' ? 'jpg' : 'png'}`);
    return;
  }
  const svg = document.querySelector<SVGSVGElement>('.surface-canvas');
  if (!svg) throw new Error('Визуализация не найдена');
  const serialized = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  const scale = 2;
  const canvas = document.createElement('canvas');
  const bounds = svg.viewBox.baseVal;
  canvas.width = bounds.width * scale;
  canvas.height = bounds.height * scale;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas недоступен');
  context.fillStyle = '#f3f1eb';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const output = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Не удалось создать файл')), `image/${format}`, 0.94),
  );
  downloadBlob(output, `${name}.${format === 'jpeg' ? 'jpg' : 'png'}`);
};

export const App = () => {
  const { project, past, future, replaceProject, updateProject, undo, redo, newProject } = useProjectStore();
  const [activePanel, setActivePanel] = useState<PanelId>('tiles');
  const [dark, setDark] = useState(true); // тёмная тема по умолчанию
  const [presentation, setPresentation] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [savedProjects, setSavedProjects] = useState<Array<{ id: string; name: string; updatedAt: string }>>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalResult, setProposalResult] = useState<ProposalResult | null>(null);
  const [proposalBuilding, setProposalBuilding] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const lastSaved = useRef('');

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2800);
  }, []);

  const refreshProjects = useCallback(async () => {
    try {
      const response = await fetch('/api/projects', { headers: clientHeaders() });
      if (!response.ok) return;
      const payload = (await response.json()) as { projects: StoredProject[] };
      setSavedProjects(payload.projects.map(({ id, name, updatedAt }) => ({ id, name, updatedAt })));
    } catch {
      // Local mode remains fully usable when the API is unavailable.
    }
  }, []);

  const saveProject = useCallback(async (quiet = false) => {
    setSaveStatus('saving');
    localStorage.setItem('kafel-lab-project', JSON.stringify(project));
    try {
      const response = await fetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: clientHeaders(true),
        body: JSON.stringify({ name: project.name, state: project }),
      });
      if (!response.ok) throw new Error('API save failed');
      lastSaved.current = project.updatedAt;
      setSaveStatus('saved');
      if (!quiet) notify('Проект сохранён');
      await refreshProjects();
    } catch {
      lastSaved.current = project.updatedAt;
      setSaveStatus('saved');
      if (!quiet) notify('Сохранено локально');
    }
  }, [notify, project, refreshProjects]);

  useEffect(() => {
    const localProject = localStorage.getItem('kafel-lab-project');
    if (localProject) {
      try {
        const parsed = migrateProject(JSON.parse(localProject) as ProjectState);
        replaceProject(parsed, false);
      } catch {
        localStorage.removeItem('kafel-lab-project');
      }
    }
    setHydrated(true);
    void refreshProjects();
  }, [refreshProjects, replaceProject]);

  useEffect(() => {
    if (!hydrated || !access?.hasAccess || project.updatedAt === lastSaved.current) return;
    setSaveStatus('unsaved');
    const timer = window.setTimeout(() => void saveProject(true), 1800);
    return () => window.clearTimeout(timer);
  }, [hydrated, access, project, saveProject]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      }
      if (event.key === 'Escape') {
        setPresentation(false);
        setExportOpen(false);
        setPanelOpen(false);
        setInspectorOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [redo, undo]);

  useEffect(() => {
    if (!exportOpen) return;
    const onPointerDown = (event: MouseEvent | PointerEvent) => {
      const target = event.target as Element | null;
      if (target && !target.closest('.export-menu')) {
        setExportOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [exportOpen]);

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  }, [dark]);

  const openProject = async (id: string) => {
    try {
      const response = await fetch(`/api/projects/${id}`, { headers: clientHeaders() });
      if (!response.ok) throw new Error();
      const payload = (await response.json()) as { project: StoredProject };
      const state = migrateProject(payload.project.state);
      replaceProject(state);
      notify('Проект открыт');
    } catch {
      notify('Не удалось открыть проект');
    }
  };

  const deleteProject = async (id: string) => {
    if (!window.confirm('Удалить проект без возможности восстановления?')) return;
    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'DELETE',
        headers: clientHeaders(true),
      });
      if (!response.ok && response.status !== 404) {
        notify('Не удалось удалить проект');
        return;
      }
      setSavedProjects((prev) => prev.filter((p) => p.id !== id));
      if (id === project.id) {
        // Удаляем и текущий открытый проект из редактора — чтобы автосохранение не создало его заново
        localStorage.removeItem('kafel-lab-project');
        newProject();
        lastSaved.current = useProjectStore.getState().project.updatedAt;
      }
      notify('Проект удалён');
    } catch {
      notify('Не удалось удалить проект');
    }
  };

  const generateVariant = () => {
    updateProject((draft) => {
      const layouts = draft.selectedSurface === 'floor' ? FLOOR_LAYOUTS : WALL_LAYOUTS;
      const current = layouts.findIndex((layout) => layout.id === draft.surfaces[draft.selectedSurface].templateId);
      draft.surfaces[draft.selectedSurface].templateId = layouts[(current + 1) % layouts.length].id;
      return draft;
    });
    notify('Создан новый вариант раскладки');
  };

  const exportPdf = async () => {
    await openProposal();
  };

  /**
   * Один клик для менеджера: собираем 3D-картинку и раскладку, формируем КП в PDF,
   * скачиваем файл и открываем окно отправки клиенту.
   */
  const openProposal = async (clientOverride?: ProposalClientInfo) => {
    setProposalOpen(true);
    setProposalBuilding(true);
    setProposalError('');
    const client: ProposalClientInfo = clientOverride ?? {
      name: localStorage.getItem('kafel-lab-client-name') ?? undefined,
      phone: localStorage.getItem('kafel-lab-client-phone') ?? undefined,
    };
    const restoreView = project.viewMode;
    try {
      let room3d = captureRoom3dDataUrl();
      if (!room3d) {
        updateProject((draft) => {
          draft.viewMode = 'room';
          return draft;
        }, false);
        // Ждём, пока three.js смонтирует canvas и отрисует первый кадр
        for (let attempt = 0; attempt < 12 && !room3d; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 400));
          room3d = captureRoom3dDataUrl();
        }
      }
      const layout = await renderLayoutDataUrl(project, project.selectedSurface);
      const result = await buildProposalRequest(project, client, { room3d, layout });
      downloadBlob(result.blob, `${project.name} — КП.pdf`);
      setProposalResult(result);
      notify('КП сформировано и скачано');
    } catch (error) {
      setProposalError(error instanceof Error && error.message
        ? `Не удалось сформировать КП: ${error.message}`
        : 'Не удалось сформировать КП');
    } finally {
      setProposalBuilding(false);
      if (restoreView !== 'room') {
        updateProject((draft) => {
          draft.viewMode = restoreView;
          return draft;
        }, false);
      }
    }
  };

  const exportFile = async (format: 'png' | 'jpeg' | 'pdf') => {
    setExportOpen(false);
    try {
      if (format === 'pdf') await exportPdf();
      else await renderSvg(format, project.name);
    } catch {
      notify('Не удалось экспортировать изображение');
    }
  };

  return (
    <div className={`app-shell ${presentation ? 'presentation-mode' : ''}`}>
      {!access || !access.hasAccess ? (
        <AuthGate onAccess={setAccess} />
      ) : (
      <>
      <header className="topbar">
        <div className="topbar-brand">
          <span className="brand-mark"><i /><i /><i /><i /></span>
          <span><strong>KAFEL</strong><b>LAB</b></span>
        </div>
        <div className="project-heading">
          <strong>{project.name}</strong>
          <span className={`save-status ${saveStatus}`}>
            <i />
            {saveStatus === 'saving' ? 'Сохранение…' : saveStatus === 'unsaved' ? 'Есть изменения' : 'Сохранено'}
          </span>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="icon-button mobile-menu"
            title="Меню"
            aria-label="Открыть меню"
            onClick={() => { setPanelOpen((value) => !value); setInspectorOpen(false); }}
          >
            <Icon name="menu" />
          </button>
          <button
            type="button"
            className="icon-button tablet-only"
            title="Панели инструментов"
            onClick={() => { setPanelOpen((value) => !value); setInspectorOpen(false); }}
          >
            <Icon name="grid" />
          </button>
          <button
            type="button"
            className="icon-button tablet-only"
            title="Свойства и расчёт"
            onClick={() => { setInspectorOpen((value) => !value); setPanelOpen(false); }}
          >
            <Icon name="layers" />
          </button>
          <div className="history-actions">
            <button type="button" onClick={undo} disabled={!past.length} title="Отменить (Ctrl+Z)"><Icon name="undo" /></button>
            <button type="button" onClick={redo} disabled={!future.length} title="Повторить (Ctrl+Y)"><Icon name="redo" /></button>
          </div>
          <button type="button" className="icon-button" onClick={() => setDark((value) => !value)} title="Сменить тему"><Icon name="moon" /></button>
          {access.reason === 'trial' && access.trialEndsAt && (
            <span className="topbar-trial" title="Пробный период">
              Триал до {new Date(access.trialEndsAt).toLocaleDateString('ru-RU')}
            </span>
          )}
          <button type="button" className="button topbar-button" onClick={() => setPresentation((value) => !value)}><Icon name="present" /> Презентация</button>
          <button
            type="button"
            className="button primary proposal-button"
            title="Сформировать коммерческое предложение и отправить клиенту"
            onClick={() => void openProposal()}
          >
            <Icon name="download" /><span>КП клиенту</span>
          </button>
          <button
            type="button"
            className="button secondary topbar-button"
            title={access.phone}
            onClick={async () => {
              if (window.confirm('Выйти из аккаунта? Несохранённые изменения будут потеряны.')) {
                await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
                window.location.reload();
              }
            }}
          >
            {access.phone.slice(-4) ? `··${access.phone.slice(-4)}` : ''} Выйти
          </button>
          <div className="export-menu">
            <button type="button" className="button primary" onClick={() => setExportOpen((value) => !value)}><Icon name="download" /> Экспорт</button>
            {exportOpen && (
              <div className="export-popover">
                <button type="button" onClick={() => void exportFile('png')}><strong>PNG</strong><span>Высокое качество</span></button>
                <button type="button" onClick={() => void exportFile('jpeg')}><strong>JPG</strong><span>Для отправки клиенту</span></button>
                <button type="button" onClick={() => void exportFile('pdf')}><strong>КП · PDF</strong><span>3D + раскладка + смета</span></button>
              </div>
            )}
          </div>
        </div>
        {presentation && <button type="button" className="exit-presentation" onClick={() => setPresentation(false)}><Icon name="x" /> Выйти</button>}
      </header>

      <div className="workspace">
        <LeftPanel
          active={activePanel}
          onActiveChange={setActivePanel}
          savedProjects={savedProjects}
          onOpenProject={(id) => void openProject(id)}
          onDeleteProject={(id) => void deleteProject(id)}
          onSave={() => void saveProject()}
          onOpenProposal={() => void openProposal()}
          open={panelOpen}
        />
        <Visualizer onGenerate={generateVariant} />
        <Inspector open={inspectorOpen} onClose={() => setInspectorOpen(false)} />
      </div>

      {(panelOpen || inspectorOpen) && (
        <button
          type="button"
          className="tablet-scrim"
          aria-label="Закрыть панель"
          onClick={() => { setPanelOpen(false); setInspectorOpen(false); }}
        />
      )}

      {proposalOpen && (
        <ProposalDialog
          project={project}
          result={proposalResult}
          building={proposalBuilding}
          buildError={proposalError}
          onClose={() => setProposalOpen(false)}
          onRebuild={(client) => void openProposal(client)}
          onNotify={notify}
        />
      )}

      {toast && <div className="toast"><Icon name="check" /> {toast}</div>}
      </>
      )}
    </div>
  );
};
